import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { TrainerAPI } from "../../api/TrainerAPI";
import BatchTableView from "./BatchTableView";
import {
  BookOpen,
  Users,
  Calendar,
  Clock,
  TrendingUp,
  Sparkles,
  Activity,
  IndianRupee,
  Receipt,
} from "lucide-react";
import ProfileEditModal from "./ProfileEditModal";

const DashboardStats = ({ showTable = false }) => {
  const { user } = useAuth();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const handleProfileSave = async (formData) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `https://trainer-portal-78f5.onrender.com/api/trainer/profile/${user.email}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const data = await response.json();
      if (response.ok) {
        alert("Profile updated successfully");
      } else {
        alert(data.error || "Update failed");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error. Please try again.");
    }
  };

  // Function to fetch and calculate total earned from invoices with "Payment Sent" status only
  const fetchTotalEarned = async () => {
    try {
      const invoices = await TrainerAPI.getTrainerInvoices();

      // Calculate total earned by summing only "Payment Sent" invoices using netPayable
      const total = invoices.reduce((sum, invoice) => {
        if (invoice.status?.toLowerCase() === "payment sent") {
          const netPayable = parseFloat(invoice.netPayable) || 0;
          return sum + netPayable;
        }
        return sum;
      }, 0);

      // Calculate invoice status counts
      const statusCounts = invoices.reduce((acc, invoice) => {
        const status = invoice.status?.toLowerCase() || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      setTotalEarned(total);
      setInvoiceStats({
        total: invoices.length,
        paymentSent: statusCounts["payment sent"] || 0,
        submitted: statusCounts["submitted"] || 0,
        approved: statusCounts["approved"] || 0,
        hold: statusCounts["hold"] || 0,
        receiptSent: statusCounts["receipt sent"] || 0,
        processing: statusCounts["processing"] || 0,
        open: statusCounts["open"] || 0,
      });
    } catch (err) {
      console.error("Error fetching total earned:", err);
      setTotalEarned(0);
      setInvoiceStats({});
    }
  };

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [statsData, batchesData] = await Promise.all([
          TrainerAPI.getDashboardStats(),
          // TrainerAPI.getBatches()
        ]);
        setStats(statsData);
        setBatches(batchesData);

        // Fetch total earned from invoices
        if (user?.email) {
          await fetchTotalEarned();
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      loadDashboard();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-t-transparent border-[#FC7A69]"></div>
            <div className="absolute inset-0 rounded-full border-2 border-[#FC7A69]/20"></div>
          </div>
          <p className="text-sm text-gray-600 font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 text-center min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-xl p-6 shadow-lg max-w-md">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Activity className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-red-700 font-medium text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Batches",
      value: stats?.totalBatches || 0,
      icon: BookOpen,
      gradient: "from-blue-500 via-blue-600 to-blue-700",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      shadowColor: "shadow-blue-200",
    },
    {
      title: "Total Students",
      value: stats?.totalStudents || 0,
      icon: Users,
      gradient: "from-emerald-500 via-emerald-600 to-emerald-700",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-600",
      shadowColor: "shadow-emerald-200",
    },
    {
      title: "Total Earned",
      value: `${totalEarned.toLocaleString("en-IN")}`,
      icon: IndianRupee,
      gradient: "from-green-500 via-green-600 to-green-700",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      shadowColor: "shadow-green-200",
      isEarnings: true,
    },
    {
      title: "Completed Batches",
      value: stats?.completedBatches || 0,
      icon: TrendingUp,
      gradient: "from-orange-500 via-orange-600 to-orange-700",
      bgColor: "bg-orange-50",
      textColor: "text-orange-600",
      shadowColor: "shadow-orange-200",
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
      {/* Compact padding for better space utilization */}
      <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
        {/* Compact Header Section */}
        <div className="mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 leading-tight">
                  Welcome {user?.profile?.name}!
                </h1>
                <div className="hidden sm:flex">
                  <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" />
                </div>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                Track your training progress and manage sessions efficiently
              </p>
            </div>
          </div>
        </div>

        {/* Compact Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {statCards.map((card, idx) => (
            <div
              key={idx}
              className={`group bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 p-3 sm:p-4 transition-all duration-300 hover:scale-[1.02] hover:${card.shadowColor}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div
                  className={`p-2 rounded-lg ${card.bgColor} group-hover:scale-110 transition-transform duration-300 shadow-sm`}
                >
                  <card.icon className={`w-4 h-4 ${card.textColor}`} />
                </div>
                <div className="text-right">
                  <p
                    className={`text-lg sm:text-xl font-bold text-gray-900 group-hover:text-[#FC7A69] transition-colors leading-none ${
                      card.isEarnings ? "text-base sm:text-lg" : ""
                    }`}
                  >
                    {card.isEarnings ? card.value : card.value.toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-600 leading-tight">
                  {card.title}
                </p>
                {card.subtitle && (
                  <p className="text-xs text-gray-500 leading-tight mt-1">
                    {card.subtitle}
                  </p>
                )}
              </div>

              {/* Animated gradient bar */}
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${card.gradient} rounded-full transition-all duration-1000 group-hover:w-full`}
                  style={{
                    width: card.isEarnings
                      ? "100%"
                      : `${Math.min((card.value || 0) * 10, 100)}%`,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Overview Section with Invoice Status replacing Avg Students/Batch */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">
                Performance Overview
              </h3>
              <p className="text-gray-600 text-xs">Key metrics at a glance</p>
            </div>
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-600">Live</span>
            </div>
          </div>

          {/* Updated Progress Indicators with Invoice Status replacing Avg Students/Batch */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <div className="text-lg font-bold text-blue-600 mb-1">
                {(
                  ((stats?.completedBatches || 0) /
                    Math.max(stats?.totalBatches || 1, 1)) *
                  100
                ).toFixed(0)}
                %
              </div>
              <div className="text-xs text-blue-600 font-medium">
                Completion Rate
              </div>
            </div>

            {/* Replaced Avg Students/Batch with Invoice Payment Status */}
            <div className="text-center p-3 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
              <div className="text-lg font-bold text-green-600 mb-1 flex items-center justify-center">
                <Receipt className="w-4 h-4 mr-1" />
                {invoiceStats.paymentSent || 0}/{invoiceStats.total || 0}
              </div>
              <div className="text-xs text-green-600 font-medium">
                Payment Sent/Total Invoice
              </div>
              {/* {invoiceStats.total > 0 && (
                <div className="text-xs text-green-500 mt-1">
                  {((invoiceStats.paymentSent || 0) / invoiceStats.total * 100).toFixed(0)}% Paid
                </div>
              )} */}
            </div>

            {/* Total Earned Amount from Payment Sent invoices */}
            <div className="text-center p-3 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 ring-2 ring-purple-200 ring-opacity-50">
              <div className="text-lg font-bold text-purple-600 mb-1 flex items-center justify-center">
                <IndianRupee className="w-4 h-4 mr-1" />
                {totalEarned.toLocaleString("en-IN")}
              </div>
              <div className="text-xs text-purple-600 font-medium">
                Total Earned
              </div>
              {/* <div className="text-xs text-purple-500 mt-1">
                Payment Sent Only
              </div> */}
            </div>
          </div>

          {/* Additional Invoice Status Breakdown */}
          {/* Additional Invoice Status Breakdown */}
          {invoiceStats.total > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Invoice Status Breakdown
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries({
                  "Payment Sent": invoiceStats.paymentSent || 0,
                  Submitted: invoiceStats.submitted || 0,
                  Approved: invoiceStats.approved || 0,
                }).map(([status, count]) => {
                  return (
                    <div
                      key={status}
                      className="text-center p-2 rounded border shadow-sm 
                       bg-gradient-to-br from-[#FC7A69]/10 via-white/50 to-[#221E45]/10 
                       border-[#FC7A69]/30"
                    >
                      <div className="text-sm font-semibold text-[#221E45]">
                        {count}
                      </div>
                      <div className="text-xs text-[#221E45]">{status}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Conditionally show batch table */}
        {showTable && (
          <div className="mt-5">
            <BatchTableView batches={batches} onBack={() => {}} />
          </div>
        )}

        {/* Compact Call-to-Action */}
        <div className="bg-gradient-to-r from-[#FC7A69]/10 via-white/50 to-[#221E45]/10 rounded-xl p-4 text-center border border-gray-200">
          <h3 className="text-base font-semibold text-gray-800 mb-1">
            Manage Your Training
          </h3>
          <p className="text-gray-600 text-xs mb-3">
            Access all batches and track student progress
          </p>
          <button
            onClick={() => (window.location.href = "/batches")}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium text-sm"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            View All Batches
          </button>
        </div>
      </div>

      <ProfileEditModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        profile={user?.profile}
        onSave={handleProfileSave}
      />
    </div>
  );
};

export default DashboardStats;
